// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, Input, OnInit, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import {
  StatementService,
  StatementResource,
  StatementAction,
  PolicyEffectType,
  Loadable,
} from '@app/core';
import {
  State,
  selectResourceActionsNeedLoading,
  StatementSelections,
  LoadingPolicyStatementServicesAction,
  LoadingPolicyStatementResourceActionsAction,
  RemoveStatementAction,
  SelectEffectAction,
  SelectServiceAction,
  SelectResourceAction,
  SelectActionActions,
  selectStatementServices,
  selectStatementResources,
  selectStatementActions,
  selectLoadableResourceActions,
} from '../../state';

@Component({
  selector: 'app-policy-statement-card',
  templateUrl: './policy-statement-card.component.html',
  styleUrls: ['./policy-statement-card.component.scss']
})
export class PolicyStatementCardComponent implements OnInit, OnDestroy {
  @Input() statement: StatementSelections = {};
  @Input() statementIdx = 0;
  @Input() showCancelButton = true;
  readonly effects = [PolicyEffectType.ALLOW, PolicyEffectType.DENY];
  readonly loadableServices$: Observable<Loadable | undefined>;
  readonly services$: Observable<StatementService[] | undefined>;
  private resourceActionSubscription: Subscription | undefined;
  selectedActions: StatementAction[] | undefined = undefined;

  constructor(private store: Store<State>) {
    this.loadableServices$ = this.store.pipe(select(selectStatementServices));
    this.services$ = this.loadableServices$.pipe(
      filter<Loadable>(loadable => loadable && loadable.data),
      map(loaded => loaded.data)
    );
    this.loadableServices$
      .pipe(filter(services => services === undefined))
      .subscribe(() => this.store.dispatch(new LoadingPolicyStatementServicesAction()));
  }

  ngOnDestroy() {
    if (this.resourceActionSubscription !== undefined) {
      this.resourceActionSubscription.unsubscribe();
    }
  }

  removeStatement() {
    this.store.dispatch(new RemoveStatementAction({ statementIdx: this.statementIdx }));
  }

  get resources$() {
    if (!this.statement.service) {
      return undefined;
    }
    return this.store.pipe(select(selectStatementResources(this.statement.service.id)));
  }

  get loadableResourceActions$() {
    if (!this.statement.service) {
      return undefined;
    }
    return this.store.pipe(select(selectLoadableResourceActions(this.statement.service.id)));
  }

  get actions$() {
    const service = this.statement.service;
    const resource = this.statement.resource;
    if (service === undefined || resource === undefined) {
      return undefined;
    }
    return this.store.pipe(select(selectStatementActions(service.id, resource.id)));
  }

  selectEffect(effect: PolicyEffectType) {
    const statementIdx = this.statementIdx;
    this.store.dispatch(new SelectEffectAction({ statementIdx, effect }));
  }

  selectService(service: StatementService) {
    const statementIdx = this.statementIdx;
    this.store.dispatch(new SelectServiceAction({ statementIdx, service }));
    this.loadResourceActionsIfNecessary(service);
  }

  selectResource(resource: StatementResource) {
    const statementIdx = this.statementIdx;
    this.store.dispatch(new SelectResourceAction({ statementIdx, resource }));
  }

  onSelectAction(actions: StatementAction[]) {
    const statementIdx = this.statementIdx;
    this.selectedActions = actions;
    this.store.dispatch(new SelectActionActions({ statementIdx, actions }));
  }

  trackByStatementIdx(idx: number) {
    return `statement${idx}`;
  }

  private loadResourceActionsIfNecessary(service: StatementService) {
    if (!this.resources$) {
      this.store.dispatch(new LoadingPolicyStatementResourceActionsAction({ serviceId: service.id }));
    }
    this.resourceActionSubscription = this.store
      .pipe(
        select(selectResourceActionsNeedLoading(service.id)),
        filter(needLoading => needLoading)
      )

      .subscribe(needLoading => {
        this.store.dispatch(new LoadingPolicyStatementResourceActionsAction({ serviceId: service.id }));
      });
  }

  ngOnInit() {}
}



// WEBPACK FOOTER //
// ./src/app/access-management/policies/policy-statement-card/policy-statement-card.component.ts